import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from '../axios';
import { permissionRider, tenantId } from '../Utils/Constants/utils';

const initialState = {
	Riders: {
		loading: false,
		ridersList: [],
		total: 0,
		allRiders: []
	},
	SelectedRider: { loading: false, rider: null },
	PendingRiders: { loading: false, ridersList: [], total: 0 },
};

export const getAllRiders = createAsyncThunk(
	'Rider/getAllRiders',
	async (arg, { rejectWithValue }) => {
		const { page = 0, size = 10, search = '', sortBy = 'created', sortDirection = 'DESC' } = arg || {};

		try {
			const params = new URLSearchParams({ page, size, sortBy, sortDirection });
			if (search) params.append('search', encodeURIComponent(search));
			const response = await axios.get(`/riders?${params}`);


			return {
				riders: response.data.content,
				total: response.data.totalElements,
			};
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message);
		}
	}
);


export const getFullListRiders = createAsyncThunk(
	'Rider/getFullListRiders',
	async (_, { rejectWithValue }) => {

		try {
			const response = await axios.get(`/riders/all`);
			return Object.values(response.data);
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message);
		}
	}
);

export const addNewRider = createAsyncThunk('Rider/addNewRider', async (data) => {
	const tmpRider = data.rider;
	const role = permissionRider;
	const rider = { ...tmpRider, tenantId, role };
	try {
		const response = await axios.post(`riders`, rider);
		return response.data;
	} catch (error) {
		const customError = {
			name: 'Custom axios error',
			message: error.response.data.message,
		};
		throw customError;
	}
});

export const getRiderById = createAsyncThunk('Rider/getRiderById', async (data) => {
	try {
		const response = await axios.get(`riders/${data.riderId}`);
		return response.data;
	} catch (error) {
		const customError = {
			name: 'Custom axios error',
			message: error.response.data.message,
		};
		throw customError;
	}
});

export const deleteRider = createAsyncThunk('Rider/deleteRider', async (data) => {
	try {
		await axios.delete(`riders/${data.riderId}/RIDER`);
		return data.riderId;
	} catch (error) {
		const customError = {
			name: 'Custom axios error',
			message: error.response.data.message,
		};
		throw customError;
	}
});

export const updateRiderData = createAsyncThunk('Rider/updateRider', async (data) => {
	try {
		const role = permissionRider;
		const rider = { ...data.rider, role };
		const response = await axios.put(`riders/${data.riderId}`, rider);
		return response.data;
	} catch (error) {
		const customError = {
			name: 'Custom axios error',
			message: error.response.data.message,
		};
		throw customError;
	}
});

export const changeRiderStatus = createAsyncThunk('Rider/changeRiderStatus', async (data) => {
	try {
		await axios.put(`users/${data.userId}/status`, { active: data.active });
		return data;
	} catch (error) {
		const customError = {
			name: 'Custom axios error',
			message: error.response.data.message,
		};
		throw customError;
	}
});

export const getPendingRiders = createAsyncThunk('Rider/getPendingRiders', async ({
	page = 0, size = 10, sortBy = 'created', sortDirection = 'DESC', search = ''
}) => {
	try {
		const params = new URLSearchParams({ page, size, sortBy, sortDirection });
		if (search) params.append('search', search);
		const response = await axios.get(`riders/pending?${params}`);
		return {
			pendingRiders: response.data.content,
			total: response.data.totalElements
		}
	} catch (error) {
		const customError = {
			name: 'Custom axios error',
			message: error.response.data.message,
		};
		throw customError;
	}
});

export const reviewRiderApplication = createAsyncThunk(
	'Rider/reviewRiderApplication',
	async ({ riderId, reviewData }, { rejectWithValue }) => {
		try {
			const response = await axios.put(`riders/${riderId}/review`, reviewData);
			// Return both the response data and the riderId for state management
			return {
				riderId,
				reviewData,
				updatedRider: response.data,
			};
		} catch (error) {
			const customError = {
				name: 'Custom axios error',
				message: error.response.data.message,
			};
			return rejectWithValue(customError);
		}
	},
);

export const batchReviewRiderApplications = createAsyncThunk(
	'Rider/batchReviewRiderApplications',
	async ({ riderIds, reviewData }, { rejectWithValue }) => {
		try {
			const response = await axios.put(`riders/batch/review`, {
				riderIds,
				...reviewData,
			});
			// Return the response data along with the input for state management
			return {
				riderIds,
				reviewData,
				result: response.data,
			};
		} catch (error) {
			const customError = {
				name: 'Custom axios error',
				message: error.response.data.message,
			};
			return rejectWithValue(customError);
		}
	},
);

export const riderSlice = createSlice({
	name: 'Rider',
	initialState,
	reducers: {},
	extraReducers: {
		[getAllRiders.pending]: (state) => {
			state.Riders.loading = true;
		},
		[getAllRiders.rejected]: (state) => {
			state.Riders.loading = false;
		},
		[getAllRiders.fulfilled]: (state, action) => {
			state.Riders.loading = false;
			state.Riders.ridersList = action.payload.riders;
			state.Riders.total = action.payload.total;
		},
		[getFullListRiders.pending]: (state) => {
			state.Riders.loading = true;
		},
		[getFullListRiders.rejected]: (state) => {
			state.Riders.loading = false;
		},
		[getFullListRiders.fulfilled]: (state, action) => {
			state.Riders.loading = false;
			state.Riders.allRiders = action.payload;
		},
		[addNewRider.pending]: (state) => {
			state.PendingRiders.loading = true;
		},
		[addNewRider.rejected]: (state) => {
			state.PendingRiders.loading = false;
		},
		[addNewRider.fulfilled]: (state, action) => {
			state.PendingRiders.loading = false;
			// Add new riders to pending list, not main riders list
			const newRider = {
				...action.payload,
				applicationStatus: 'PENDING',
			};
			// Add to PendingRiders instead of Riders
			state.PendingRiders.ridersList.push(newRider);
		},
		[getRiderById.pending]: (state) => {
			state.SelectedRider.loading = true;
		},
		[getRiderById.rejected]: (state) => {
			state.SelectedRider.loading = false;
		},
		[getRiderById.fulfilled]: (state, action) => {
			state.SelectedRider.loading = false;
			state.SelectedRider.rider = action.payload;
		},
		[deleteRider.pending]: (state) => {
			state.Riders.loading = true;
		},
		[deleteRider.rejected]: (state) => {
			state.Riders.loading = false;
		},
		[deleteRider.fulfilled]: (state, action) => {
			state.Riders.loading = false;
			state.Riders.ridersList = state.Riders.ridersList.filter((item) => {
				return item.id !== action.payload;
			});
		},
		[updateRiderData.pending]: (state) => {
			state.Riders.loading = true;
		},
		[updateRiderData.rejected]: (state) => {
			state.Riders.loading = false;
		},
		[updateRiderData.fulfilled]: (state, action) => {
			state.Riders.loading = false;
			state.Riders.ridersList = state.Riders.ridersList.map((item) => {
				if (item.id === action.payload.id) {
					return action.payload;
				}
				return item;
			});
			// Also update SelectedRider if it's the same rider being updated
			if (state.SelectedRider.rider?.id === action.payload.id) {
				state.SelectedRider.rider = action.payload;
			}
		},
		[changeRiderStatus.pending]: (state) => {
			state.Riders.loading = true;
		},
		[changeRiderStatus.fulfilled]: (state, action) => {
			state.Riders.loading = false;
			state.Riders.ridersList = state.Riders.ridersList.map((item) => {
				if (item.id === action.payload.userId) {
					item = { ...item, active: action.payload.active };
					return item;
				}
				return item;
			});
		},
		// getPendingRiders
		[getPendingRiders.pending]: (state) => {
			state.PendingRiders.loading = true;
		},
		[getPendingRiders.rejected]: (state) => {
			state.PendingRiders.loading = false;
		},
		[getPendingRiders.fulfilled]: (state, action) => {
			state.PendingRiders.loading = false;
			state.PendingRiders.ridersList = action.payload.pendingRiders || [];
			state.PendingRiders.total = action.payload.total || 0;
		},
		// reviewRiderApplication
		[reviewRiderApplication.pending]: (state) => {
			state.PendingRiders.loading = true;
		},
		[reviewRiderApplication.rejected]: (state) => {
			state.PendingRiders.loading = false;
		},
		[reviewRiderApplication.fulfilled]: (state, action) => {
			state.PendingRiders.loading = false;
			const { riderId, reviewData } = action.payload;
			// Find the rider before removing from pending list
			const pendingRider = state.PendingRiders.ridersList.find(rider => rider.id === riderId);

			// Remove rider from pending list
			state.PendingRiders.ridersList = state.PendingRiders.ridersList.filter(
				rider => rider.id !== riderId
			);

			// If approved, add to main riders list
			if (reviewData.approved && pendingRider) {
				const approvedRider = {
					...pendingRider,
					applicationStatus: 'APPROVED',
					active: true,
				};
				state.Riders.ridersList.push(approvedRider);
			}
		},
		// batchReviewRiderApplications
		[batchReviewRiderApplications.pending]: (state) => {
			state.PendingRiders.loading = true;
		},
		[batchReviewRiderApplications.rejected]: (state) => {
			state.PendingRiders.loading = false;
		},
		[batchReviewRiderApplications.fulfilled]: (state, action) => {
			state.PendingRiders.loading = false;
			const { riderIds, reviewData } = action.payload;

			// Find riders before removing from pending list
			const pendingRiders = state.PendingRiders.ridersList.filter(rider =>
				riderIds.includes(rider.id)
			);

			// Remove reviewed riders from pending list
			state.PendingRiders.ridersList = state.PendingRiders.ridersList.filter(
				rider => !riderIds.includes(rider.id)
			);

			// If approved, add to main riders list
			if (reviewData.approved) {
				const approvedRiders = pendingRiders.map(rider => ({
					...rider,
					applicationStatus: 'APPROVED',
					active: true,
				}));
				state.Riders.ridersList.push(...approvedRiders);
			}
		},
	},
});

export default riderSlice.reducer;
